import React, { useState, memo } from 'react';
import { Card, Form, Row, Col, Input, Switch, Typography, Divider, DatePicker, Space, Button, Tag, Alert, InputNumber, Collapse, TimePicker, Modal } from 'antd';
import { PlusOutlined, CloseOutlined, EditOutlined, CalendarOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Company, CustomHoliday } from '../../../../types/company.types';
import moment, { Moment } from 'moment';
import { officialHolidays, holidayDates } from "../../../../constants/holidays";
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import locale from 'antd/lib/date-picker/locale/tr_TR';

dayjs.locale('tr');

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface BasicInfoFormProps {
  form: any;
  companyData: Company | null;
  loading: boolean;
}

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({ form, companyData, loading }) => {
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const customHolidayDates = Form.useWatch(['settings', 'customHolidayDates'], form);
  const customHolidays = Form.useWatch(['settings', 'customHolidays'], form) || [];
  const nationalHolidayDates = Form.useWatch(['settings', 'nationalHolidayDates'], form);
  
  // Özel tatil ekleme modal state'leri
  const [isAddHolidayModalVisible, setIsAddHolidayModalVisible] = useState(false);
  const [holidayForm] = Form.useForm();
  const [isFullDay, setIsFullDay] = useState(true);

  // New state management for religious holidays
  const years = ['2025', '2026', '2027', '2028', '2029'];
  const initialEditingState = years.reduce((acc, year) => ({ ...acc, [year]: false }), {});
  
  const [editingStates, setEditingStates] = useState<{ [holidayType: string]: { [year: string]: boolean } }>({
    ramadan: { ...initialEditingState },
    eid: { ...initialEditingState },
  });

  const [dateStates, setDateStates] = useState<{ [year: string]: { [holidayType: string]: { start: Moment | null, end: Moment | null } } }>(
    years.reduce((acc, year) => ({
      ...acc,
      [year]: {
        ramadan: { start: null as Moment | null, end: null as Moment | null },
        eid: { start: null as Moment | null, end: null as Moment | null },
      }
    }), {})
  );

  const handleReligiousHolidayEdit = (type: 'ramadan' | 'eid', year: string, isEditing: boolean) => {
    setEditingStates(prev => ({
      ...prev,
      [type]: { ...prev[type], [year]: isEditing }
    }));
    if (!isEditing) {
      setDateStates((prev: any) => ({
        ...prev,
        [year]: { ...prev[year], [type]: { start: null, end: null } }
      }));
    }
  };

  const handleReligiousDateChange = (type: 'ramadan' | 'eid', year: string, dateType: 'start' | 'end', date: Moment | null) => {
    setDateStates((prev: any) => ({
      ...prev,
      [year]: {
        ...prev[year],
        [type]: { ...prev[year][type], [dateType]: date }
      }
    }));
  };
  
  const generateDateRange = (startDate: Moment, endDate: Moment): string[] => {
    const dates: string[] = [];
    let currentDate = startDate.clone().startOf('day');
    const end = endDate.clone().startOf('day');
    
    // Using clone() in each iteration to prevent mutation bugs causing infinite loops.
    while (currentDate.isSameOrBefore(end, 'day')) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate = currentDate.clone().add(1, 'day');
    }
    return dates;
  };

  const handleApplyReligiousDates = (type: 'ramadan' | 'eid', year: string) => {
    const { start, end } = dateStates[year][type];
    if (!start || !end || end.isBefore(start)) return;

    const newDateRange = generateDateRange(start, end);
    const fieldName = type === 'ramadan' ? 'ramadanHolidayDates' : 'eidAlAdhaHolidayDates';
    
    const allDates = form.getFieldValue(['settings', fieldName]) || [];
    const datesFromOtherYears = allDates.filter((d: string) => !d.startsWith(year));
    const finalDates = [...datesFromOtherYears, ...newDateRange].sort();

    form.setFieldValue(['settings', fieldName], finalDates);
    handleReligiousHolidayEdit(type, year, false);
  };

  // Özel tatil ekleme fonksiyonu (yeni format)
  const handleAddCustomHoliday = () => {
    if (!selectedDate) {
      setIsAddHolidayModalVisible(true);
      return;
    }
    
    // Eski format için geriye dönük uyumluluk
    const dateString = selectedDate.format('YYYY-MM-DD');
    const currentDates = customHolidayDates || [];
    
    if (currentDates.includes(dateString)) {
      return;
    }
    
    const newDates = [...currentDates, dateString].sort();
    form.setFieldValue(['settings', 'customHolidayDates'], newDates);
    setSelectedDate(null);
  };

  // Yeni özel tatil ekleme fonksiyonu (customHolidays formatı)
  const handleAddNewCustomHoliday = () => {
    holidayForm.validateFields().then((values) => {
      const newHoliday: CustomHoliday = {
        date: values.date.format('YYYY-MM-DD'),
        description: values.description || '',
        isFullDay: values.isFullDay !== false,
        startTime: values.isFullDay !== false ? undefined : (values.startTime ? values.startTime.format('HH:mm') : undefined),
        endTime: values.isFullDay !== false ? undefined : (values.endTime ? values.endTime.format('HH:mm') : undefined),
      };

      const currentHolidays = customHolidays || [];
      
      // Aynı tarih zaten varsa güncelle, yoksa ekle
      const existingIndex = currentHolidays.findIndex((h: CustomHoliday) => h.date === newHoliday.date);
      let updatedHolidays: CustomHoliday[];
      
      if (existingIndex >= 0) {
        updatedHolidays = [...currentHolidays];
        updatedHolidays[existingIndex] = newHoliday;
      } else {
        updatedHolidays = [...currentHolidays, newHoliday].sort((a, b) => a.date.localeCompare(b.date));
      }

      form.setFieldValue(['settings', 'customHolidays'], updatedHolidays);
      holidayForm.resetFields();
      setIsAddHolidayModalVisible(false);
      setIsFullDay(true);
      setSelectedDate(null);
    }).catch((error) => {
      console.error('Form validation failed:', error);
    });
  };

  const handleRemoveCustomHoliday = (dateToRemove: string) => {
    // Yeni format için silme
    const currentHolidays = customHolidays || [];
    const updatedHolidays = currentHolidays.filter((h: CustomHoliday) => h.date !== dateToRemove);
    form.setFieldValue(['settings', 'customHolidays'], updatedHolidays);
    
    // Eski format için geriye dönük uyumluluk
    const currentDates = customHolidayDates || [];
    const newDates = currentDates.filter((date: string) => date !== dateToRemove);
    form.setFieldValue(['settings', 'customHolidayDates'], newDates);
  };

  // Özel tatil düzenleme fonksiyonu
  const handleEditCustomHoliday = (holiday: CustomHoliday) => {
    setIsFullDay(holiday.isFullDay);
    holidayForm.setFieldsValue({
      date: moment(holiday.date),
      description: holiday.description,
      isFullDay: holiday.isFullDay,
      startTime: holiday.startTime ? moment(holiday.startTime, 'HH:mm') : null,
      endTime: holiday.endTime ? moment(holiday.endTime, 'HH:mm') : null,
    });
    setIsAddHolidayModalVisible(true);
  };

  const handleHolidayToggle = (holidayId: string, year: string, checked: boolean) => {
    const holidayDate = holidayDates[year][holidayId];
    const currentDates = form.getFieldValue(['settings', 'nationalHolidayDates']) || [];
    let newDates;

    if (checked) {
      newDates = Array.from(new Set([...currentDates, holidayDate]));
    } else {
      newDates = currentDates.filter((date: string) => date !== holidayDate);
    }
    form.setFieldValue(['settings', 'nationalHolidayDates'], newDates.sort());
  };

  const renderReligiousHolidayPanel = (type: 'ramadan' | 'eid', title: string, allDates: string[]) => {
    return (
      <Panel header={title} key={type}>
        <Collapse accordion>
          {years.map(year => {
            const datesForYear = allDates.filter(d => d.startsWith(year));
            const isEditing = editingStates[type][year];
            const { start, end } = dateStates[year][type];

            return (
              <Panel header={`${year} Yılı Ayarları`} key={`${type}-${year}`}>
                <Card 
                  hoverable={!isEditing}
                  style={{ 
                    background: datesForYear.length > 0 ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)' : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                    border: datesForYear.length > 0 ? '2px solid #52c41a' : '2px solid #e8e8e8',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                   {/* This is the same card UI as before, adapted for the new state system */}
                   <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                     <Row align="middle" justify="space-between">
                       <Col>
                         <Space>
                           <div style={{width: '40px', height: '40px', borderRadius: '10px', background: datesForYear.length > 0 ? '#52c41a' : '#d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s'}}>
                             <CalendarOutlined style={{ fontSize: '20px', color: 'white' }} />
                           </div>
                           <div>
                             <Text strong style={{ fontSize: '16px', display: 'block' }}>{year} {title}</Text>
                             {datesForYear.length > 0 ? (
                               <Text type="secondary" style={{ fontSize: '13px' }}>{datesForYear.length} gün tatil</Text>
                             ) : (
                               <Text type="secondary" style={{ fontSize: '13px' }}>Tarih belirtilmemiş</Text>
                             )}
                           </div>
                         </Space>
                       </Col>
                     </Row>

                     {datesForYear.length > 0 && !isEditing && (
                       <div style={{ padding: '12px', background: 'rgba(255, 255, 255, 0.7)', borderRadius: '8px', marginTop: '8px' }}>
                         <Text style={{ fontSize: '14px' }}>
                           📅 {moment(datesForYear[0]).format('DD MMMM YYYY')} - {moment(datesForYear[datesForYear.length - 1]).format('DD MMMM YYYY')}
                         </Text>
                       </div>
                     )}

                     {!isEditing ? (
                       <Button type={datesForYear.length > 0 ? 'default' : 'primary'} block size="large" icon={datesForYear.length > 0 ? <EditOutlined /> : <PlusOutlined />} onClick={() => handleReligiousHolidayEdit(type, year, true)} disabled={loading} style={{ marginTop: '8px', borderRadius: '8px', height: '44px', fontWeight: '500' }}>
                         {datesForYear.length > 0 ? 'Tarihleri Değiştir' : 'Tarih Belirle'}
                       </Button>
                     ) : (
                       <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: '8px' }}>
                         <div style={{ padding: '16px', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '10px', border: '1px dashed #d9d9d9' }}>
                           <Row gutter={[12, 12]}>
                             <Col span={24}>
                               <Text style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>Başlangıç Tarihi</Text>
                               <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} disabled={loading} format="DD/MM/YYYY" placeholder="Başlangıç tarihi seçin" value={start} onChange={(date) => handleReligiousDateChange(type, year, 'start', date)} />
                             </Col>
                             <Col span={24}>
                               <Text style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>Bitiş Tarihi</Text>
                               <DatePicker size="large" style={{ width: '100%', borderRadius: '8px' }} disabled={loading || !start} format="DD/MM/YYYY" placeholder="Bitiş tarihi seçin" value={end} onChange={(date) => handleReligiousDateChange(type, year, 'end', date)} />
                             </Col>
                           </Row>
                         </div>
                         <Row gutter={8}>
                           <Col span={12}>
                             <Button block size="large" onClick={() => handleReligiousHolidayEdit(type, year, false)} disabled={loading} style={{ borderRadius: '8px', height: '44px' }}>İptal</Button>
                           </Col>
                           <Col span={12}>
                             <Button type="primary" block size="large" onClick={() => handleApplyReligiousDates(type, year)} disabled={loading || !start || !end} style={{ borderRadius: '8px', height: '44px', fontWeight: '500' }}>Uygula</Button>
                           </Col>
                         </Row>
                       </Space>
                     )}
                   </Space>
                </Card>
              </Panel>
            );
          })}
        </Collapse>
      </Panel>
    );
  };


  return (
    <>
          <Alert
            message="Değişikliklerinizi kaydetmeyi unutmayın!"
            description="Yaptığınız değişikliklerden sonra sayfanın en altındaki 'Ayarları Kaydet' butonuna basarak kaydedin."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ borderRadius: '8px' }}
          />
      <Card className="setting-card mt-4" title="Temel Bilgiler">

        {/* Basic Info Fields */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Şirket Adı" name="name" rules={[{ required: true, message: 'Şirket adı zorunludur' }]}>
              <Input size="large" placeholder="Şirket adını giriniz" disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="E-posta" name="email" rules={[{ required: true, message: 'E-posta zorunludur' }, { type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }]}>
              <Input size="large" placeholder="E-posta adresini giriniz" disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Telefon" name="phone" rules={[{ required: true, message: 'Telefon numarası zorunludur' }]}>
              <Input size="large" placeholder="Telefon numarasını giriniz" disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Adres" name="address" rules={[{ required: true, message: 'Adres zorunludur' }]}>
              <Input size="large" placeholder="Adresi giriniz" disabled={loading} />
            </Form.Item>
          </Col>
        </Row>
      </Card>


      <Card className="setting-card mt-4" title="Tatil Ayarları">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
 
          
          {/* Hidden Form Items */}
          <Form.Item name={['settings', 'ramadanHolidayDates']} hidden><Input /></Form.Item>
          <Form.Item name={['settings', 'eidAlAdhaHolidayDates']} hidden><Input /></Form.Item>
          <Form.Item name={['settings', 'nationalHolidayDates']} hidden><Input /></Form.Item>
          
          {/* Dini Bayramlar Bölümü */}
          <div>
            <Title level={5} style={{ fontSize: '16px', marginBottom: '16px' }}>
              Dini Bayramlar
            </Title>
            <Collapse accordion>
              {renderReligiousHolidayPanel('ramadan', 'Ramazan Bayramı', form.getFieldValue(['settings', 'ramadanHolidayDates']) || [])}
              {renderReligiousHolidayPanel('eid', 'Kurban Bayramı', form.getFieldValue(['settings', 'eidAlAdhaHolidayDates']) || [])}
            </Collapse>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Resmi Tatiller Bölümü */}
          <div>
            <Title level={5} style={{ fontSize: '16px', marginBottom: '8px' }}>
              Resmi Tatillerde Çalışma Durumu
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '13px' }}>
              "Tatil Günü" olarak işaretlenen günler puantajda resmi tatil olarak sayılacaktır.
            </Text>

            <Collapse accordion>
              {officialHolidays.map(holiday => (
                <Panel header={`${holiday.name} - ${holiday.date}`} key={holiday.id}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {['2025', '2026', '2027', '2028', '2029'].map(year => {
                      const holidayDate = holidayDates[year][holiday.id];
                      // Use the watched value directly for immediate UI feedback
                      const isHoliday = nationalHolidayDates?.includes(holidayDate);

                      return (
                        <div key={`${holiday.id}-${year}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <Text>{`${year} - ${dayjs(holidayDate).format('DD MMMM YYYY, dddd')}`}</Text>
                          <Switch
                            checked={isHoliday}
                            onChange={(checked) => handleHolidayToggle(holiday.id, year, checked)}
                            checkedChildren="Tatil Günü"
                            unCheckedChildren="Çalışma Günü"
                            disabled={loading}
                          />
                        </div>
                      );
                    })}
                  </Space>
                </Panel>
              ))}
            </Collapse>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Özel Tatiller Bölümü */}
          <div>
            <Title level={5} style={{ fontSize: '16px', marginBottom: '8px' }}>
              Özel Tatil Günleri
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '13px' }}>
              Şirketinize özel tatil günlerini buradan ekleyebilirsiniz. Tam gün veya belirli saatler arası tatil tanımlayabilirsiniz.
            </Text>

            {/* Hidden form items */}
            <Form.Item name={['settings', 'customHolidayDates']} hidden>
              <Input />
            </Form.Item>
            <Form.Item name={['settings', 'customHolidays']} hidden>
              <Input />
            </Form.Item>

            <div style={{
              padding: '20px',
              background: '#fafafa',
              borderRadius: '12px',
              border: '1px solid #e8e8e8'
            }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  holidayForm.resetFields();
                  setIsFullDay(true);
                  setIsAddHolidayModalVisible(true);
                }}
                disabled={loading}
                size="large"
                style={{
                  marginBottom: '20px',
                  borderRadius: '8px',
                  fontWeight: '500'
                }}
              >
                Yeni Özel Tatil Ekle
              </Button>

              {/* Yeni format: customHolidays */}
              {customHolidays && customHolidays.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '16px',
                    gap: '8px'
                  }}>
                    <CalendarOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                    <Text strong style={{ 
                      fontSize: '15px',
                      color: '#262626'
                    }}>
                      Eklenen Özel Tatiller ({customHolidays.length} gün)
                    </Text>
                  </div>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {customHolidays.map((holiday: CustomHoliday) => (
                      <div
                        key={holiday.date}
                        style={{
                          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                          borderRadius: '12px',
                          border: '1px solid #e8e8e8',
                          padding: '20px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {/* Sol tarafta renkli çizgi */}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            background: holiday.isFullDay 
                              ? 'linear-gradient(180deg, #52c41a 0%, #73d13d 100%)' 
                              : 'linear-gradient(180deg, #1890ff 0%, #40a9ff 100%)',
                            borderRadius: '12px 0 0 12px'
                          }}
                        />
                        
                        <Row gutter={[16, 12]} style={{ marginLeft: '12px' }}>
                          {/* Tarih ve gün */}
                          <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                              <div>
                                <Text strong style={{ 
                                  fontSize: '16px',
                                  color: '#262626',
                                  display: 'block',
                                  marginBottom: '4px'
                                }}>
                                  {moment(holiday.date).format('DD MMMM YYYY')}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                  {moment(holiday.date).format('dddd')}
                                </Text>
                              </div>
                              
                              {/* Action buttons */}
                              <Space>
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditCustomHoliday(holiday)}
                                  style={{
                                    color: '#1890ff',
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#e6f7ff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  Düzenle
                                </Button>
                                <Button
                                  type="text"
                                  danger
                                  icon={<CloseOutlined />}
                                  onClick={() => handleRemoveCustomHoliday(holiday.date)}
                                  style={{
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#fff1f0';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  Sil
                                </Button>
                              </Space>
                            </div>
                          </Col>

                          {/* Açıklama */}
                          {holiday.description && (
                            <Col span={24}>
                              <div style={{
                                padding: '10px 14px',
                                background: '#f5f5f5',
                                borderRadius: '8px',
                                borderLeft: '3px solid #1890ff'
                              }}>
                                <Text style={{ fontSize: '14px', color: '#595959' }}>
                                  {holiday.description}
                                </Text>
                              </div>
                            </Col>
                          )}

                          {/* Tatil tipi ve saat bilgisi */}
                          <Col span={24}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '12px',
                              flexWrap: 'wrap'
                            }}>
                              <Tag
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: '20px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  border: 'none',
                                  margin: 0,
                                  background: holiday.isFullDay 
                                    ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' 
                                    : 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                              >
                                {holiday.isFullDay ? '✓ Tam Gün' : '⏰ Kısmi Tatil'}
                              </Tag>
                              
                              {!holiday.isFullDay && holiday.startTime && holiday.endTime && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 14px',
                                  background: '#f0f7ff',
                                  borderRadius: '20px',
                                  border: '1px solid #bae7ff'
                                }}>
                                  <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                                  <Text strong style={{ 
                                    fontSize: '14px',
                                    color: '#1890ff',
                                    fontFamily: 'monospace'
                                  }}>
                                    {holiday.startTime} - {holiday.endTime}
                                  </Text>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </Space>
                </div>
              )}

              {/* Eski format: customHolidayDates (geriye dönük uyumluluk) */}
              {(!customHolidays || customHolidays.length === 0) && customHolidayDates && customHolidayDates.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <Text strong style={{ 
                    display: 'block', 
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}>
                    📌 Eklenen Özel Tatiller ({customHolidayDates.length} gün)
                  </Text>
                  <Space wrap size={[8, 8]}>
                    {customHolidayDates.map((date: string) => (
                      <Tag
                        key={date}
                        closable
                        onClose={() => handleRemoveCustomHoliday(date)}
                        color="blue"
                        style={{ 
                          fontSize: '14px', 
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontWeight: '500'
                        }}
                      >
                        {moment(date).format('DD MMMM YYYY')}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          </div>

          {/* Özel Tatil Ekleme/Düzenleme Modal */}
          <Modal
            title="Özel Tatil Ekle"
            open={isAddHolidayModalVisible}
            onCancel={() => {
              setIsAddHolidayModalVisible(false);
              holidayForm.resetFields();
              setIsFullDay(true);
            }}
            onOk={handleAddNewCustomHoliday}
            okText="Kaydet"
            cancelText="İptal"
            width={600}
            destroyOnClose
          >
            <Form
              form={holidayForm}
              layout="vertical"
              initialValues={{
                isFullDay: true,
              }}
            >
              <Form.Item
                name="date"
                label="Tatil Tarihi"
                rules={[{ required: true, message: 'Lütfen tatil tarihini seçin' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Tatil tarihi seçin"
                  disabled={loading}
                  locale={locale}
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Açıklama"
              >
                <Input
                  placeholder="Tatil açıklaması (örn: Şirket kuruluş günü)"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                name="isFullDay"
                label="Tatil Tipi"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Tam Gün"
                  unCheckedChildren="Kısmi Tatil"
                  checked={isFullDay}
                  onChange={(checked) => {
                    setIsFullDay(checked);
                    holidayForm.setFieldValue('isFullDay', checked);
                    if (checked) {
                      // Tam gün seçildiğinde saatleri temizle
                      holidayForm.setFieldValue('startTime', null);
                      holidayForm.setFieldValue('endTime', null);
                    }
                  }}
                  disabled={loading}
                />
              </Form.Item>

              {!isFullDay && (
                <>
                  <Form.Item
                    name="startTime"
                    label="Başlangıç Saati"
                    rules={[
                      { required: true, message: 'Lütfen başlangıç saatini seçin' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const endTime = getFieldValue('endTime');
                          if (!value) {
                            return Promise.resolve();
                          }
                          if (endTime && moment(value, 'HH:mm').isSameOrAfter(moment(endTime, 'HH:mm'))) {
                            return Promise.reject(new Error('Başlangıç saati bitiş saatinden önce olmalıdır'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      format="HH:mm"
                      placeholder="Başlangıç saati seçin"
                      disabled={loading}
                    />
                  </Form.Item>

                  <Form.Item
                    name="endTime"
                    label="Bitiş Saati"
                    rules={[
                      { required: true, message: 'Lütfen bitiş saatini seçin' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const startTime = getFieldValue('startTime');
                          if (!value) {
                            return Promise.resolve();
                          }
                          if (startTime && moment(value, 'HH:mm').isSameOrBefore(moment(startTime, 'HH:mm'))) {
                            return Promise.reject(new Error('Bitiş saati başlangıç saatinden sonra olmalıdır'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      format="HH:mm"
                      placeholder="Bitiş saati seçin"
                      disabled={loading}
                    />
                  </Form.Item>
                </>
              )}
            </Form>
          </Modal>

        </Space>
      </Card>
    </>
  );
};

export default memo(BasicInfoForm);
